import { zodResolver } from '@hookform/resolvers/zod';
import {
  inspectionsControllerDeleteAttachment,
  inspectionsControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  CloudUpload,
  Download,
  FileText,
  Image,
  Lock,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormSection } from '../../../components/forms/form-section';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb } from '../../../components/layout/breadcrumb';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

export const Route = createFileRoute('/inspections/$inspectionId/edit')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('permit:create-inspection')) throw redirect({ to: '/dashboard' });
  },
  component: InspectionEditPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  signedUrl?: string | null;
  uploadedAt: string;
  uploadedBy: { name: string };
}

interface InspectionDetail {
  id: string;
  status: string;
  findings: string | null;
  recommendations: string | null;
  assignedInspectorId: string;
  request: { id: string; referenceNo: string };
  attachments: Attachment[];
}

// ── API fetch ─────────────────────────────────────────────────────────────────

async function fetchInspection(id: string): Promise<InspectionDetail> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/v1/inspections/${id}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load inspection');
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') return <FileText size={16} className="text-red-500" />;
  if (mimeType.startsWith('image/')) return <Image size={16} className="text-blue-500" />;
  return <FileText size={16} className="text-gray-400" />;
}

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  findings: z.string(),
  recommendations: z.string(),
});
type FormValues = z.infer<typeof schema>;

// ── Page ──────────────────────────────────────────────────────────────────────

function InspectionEditPage() {
  const { inspectionId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuthStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const {
    data: inspection,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: () => fetchInspection(inspectionId),
  });

  const { register, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      findings: inspection?.findings ?? '',
      recommendations: inspection?.recommendations ?? '',
    },
  });

  // Guard: only assigned inspector in IN_PROGRESS can edit
  const isEditable =
    inspection?.status === 'IN_PROGRESS' && inspection.assignedInspectorId === user?.id;

  const saveMutation = useMutation({
    mutationFn: (data: FormValues) =>
      inspectionsControllerUpdate({
        permitId: inspection!.request.id,
        requestBody: { findings: data.findings, recommendations: data.recommendations },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      navigate({ to: '/inspections/$inspectionId', params: { inspectionId } });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      inspectionsControllerDeleteAttachment({
        permitId: inspection!.request.id,
        attachmentId,
      }),
    onSuccess: () => {
      setDeleteConfirm(null);
      qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files || !inspection) return;
    setUploadError(null);
    for (const file of Array.from(files)) {
      if (!ACCEPTED_MIME.includes(file.type)) {
        setUploadError(`"${file.name}" is not a supported file type (PDF, JPG, PNG).`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`api/v1/requests/${inspection.request.id}/inspections/attachments`, {
          body: fd,
        });
        qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      } catch {
        setUploadError(`Failed to upload "${file.name}". Please try again.`);
      } finally {
        setUploading(false);
      }
    }
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !inspection) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <AlertCircle size={40} className="text-error-400" />
          <p className="text-sm font-semibold text-gray-600">Inspection not found</p>
        </div>
      </AppLayout>
    );
  }

  if (!isEditable) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Lock size={40} className="text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">This inspection cannot be edited</p>
          <p className="text-xs text-gray-400">
            Only the assigned inspector can edit an in-progress inspection.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/inspections/$inspectionId', params: { inspectionId } })}
          >
            Back to Inspection
          </Button>
        </div>
      </AppLayout>
    );
  }

  const breadcrumbItems = [
    { label: 'Inspections', href: '/inspections' },
    { label: inspection.request.referenceNo, href: `/inspections/${inspectionId}` },
    { label: 'Edit' },
  ];

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Header ── */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Edit Inspection</h1>
          <p className="text-sm text-gray-500 mt-0.5">{inspection.request.referenceNo}</p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/inspections/$inspectionId', params: { inspectionId } })}
          >
            <X size={14} />
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={saveMutation.isPending}
            onClick={handleSubmit((d) => saveMutation.mutate(d))}
          >
            <Save size={14} />
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {saveMutation.isError && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
          <AlertCircle size={15} className="shrink-0" />
          Failed to save. Please try again.
        </div>
      )}

      <div className="space-y-4">
        {/* Findings */}
        <FormSection
          icon={<ClipboardCheck size={16} className="text-teal-600" />}
          iconBg="bg-teal-50 text-teal-600"
          title="Findings"
          subtitle="Record your field observations"
        >
          <textarea
            rows={6}
            placeholder="Describe what you observed during the inspection…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all"
            {...register('findings')}
          />
        </FormSection>

        {/* Recommendations */}
        <FormSection
          icon={<CheckCircle2 size={16} className="text-emerald-600" />}
          iconBg="bg-emerald-50 text-emerald-600"
          title="Recommendations"
          subtitle="List action items and corrective measures"
        >
          <textarea
            rows={6}
            placeholder="What actions should be taken based on your findings…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
            {...register('recommendations')}
          />
        </FormSection>

        {/* Attachments */}
        <FormSection
          icon={<CloudUpload size={16} className="text-violet-600" />}
          iconBg="bg-violet-50 text-violet-600"
          title="Attachments"
          subtitle="PDF, JPG, PNG — max 10 MB per file"
        >
          {/* Drop zone */}
          <div
            className={cn(
              'mb-4 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-all cursor-pointer',
              isDragOver
                ? 'border-teal-400 bg-teal-50'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100',
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm">
              {uploading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
              ) : (
                <CloudUpload size={20} className="text-gray-400" />
              )}
            </div>
            <p className="text-sm font-medium text-gray-600">
              {uploading ? 'Uploading…' : 'Drop files here or click to browse'}
            </p>
            <p className="text-xs text-gray-400">PDF, JPG, PNG supported</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {uploadError && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-2.5 text-xs text-error-700">
              <AlertCircle size={13} className="shrink-0" />
              {uploadError}
            </div>
          )}

          {/* Attachment list */}
          {inspection.attachments.length > 0 && (
            <div className="space-y-2">
              {inspection.attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3"
                >
                  <FileIcon mimeType={att.mimeType} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{att.fileName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {fmtSize(att.fileSizeBytes)} · {att.uploadedBy.name} ·{' '}
                      {fmtDate(att.uploadedAt)}
                    </p>
                  </div>
                  {att.signedUrl && (
                    <a
                      href={att.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors"
                      title="Download"
                    >
                      <Download size={14} />
                    </a>
                  )}
                  {deleteConfirm === att.id ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(att.id)}
                        disabled={deleteMutation.isPending}
                        className="rounded-lg bg-error-500 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-error-600 transition-colors disabled:opacity-50"
                      >
                        {deleteMutation.isPending ? '…' : 'Delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(null)}
                        className="rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(att.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-error-200 hover:bg-error-50 hover:text-error-500 transition-colors"
                      title="Delete attachment"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </FormSection>
      </div>
    </AppLayout>
  );
}
